<template>
  <div class="clients-analysis py-3" v-if="mixinUserInRoles(['Admin'])">
    <div class="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
      <div>
        <div class="text-uppercase text-muted fw-semibold report-eyebrow">Clients</div>
        <h1 class="h3 mb-1">Clients analysis</h1>
        <p class="text-muted mb-0">
          Live from Airtable · gross revenue by order start date · cancelled orders excluded
        </p>
      </div>

      <div class="d-flex align-items-center gap-2">
        <label class="small text-muted" for="analysis-year">Year</label>
        <select
          id="analysis-year"
          v-model.number="selectedYear"
          class="form-select form-select-sm year-select"
          :disabled="loading"
          @change="changeYear"
        >
          <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
        </select>
        <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="fetchReport">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
          Refresh
        </button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="loading || !orders.length" @click="downloadCsv">
          <i class="bi bi-download me-1"></i> CSV
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <strong>Could not load the report.</strong> {{ errorMessage }}
    </div>

    <div v-if="loading" class="report-loading border rounded bg-white">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading client analysis</span>
      </div>
      <div class="mt-3 text-muted">Calculating {{ selectedYear }} from live records…</div>
    </div>

    <template v-else-if="!errorMessage">
      <section aria-labelledby="year-summary-title" class="mb-4">
        <div class="d-flex justify-content-between align-items-baseline mb-2">
          <h2 id="year-summary-title" class="h5 mb-0">{{ selectedYear }} summary</h2>
          <span class="small text-muted">Average uses {{ monthsElapsed }} {{ monthsElapsed === 1 ? 'month' : 'months' }}</span>
        </div>

        <div class="kpi-grid">
          <article v-for="kpi in primaryKpis" :key="kpi.label" class="kpi-card border rounded bg-white" :title="kpi.help">
            <div class="kpi-label">{{ kpi.label }}</div>
            <div class="kpi-value">{{ kpi.value }}</div>
            <div v-if="kpi.note" class="kpi-note">{{ kpi.note }}</div>
          </article>
        </div>
      </section>

      <section class="border rounded bg-white mb-4 overflow-hidden" aria-labelledby="monthly-overview-title">
        <div class="section-heading d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h2 id="monthly-overview-title" class="h5 mb-1">Monthly overview</h2>
            <div class="small text-muted">A client is new only in the month of its first order.</div>
          </div>
          <span class="small text-muted">Updated {{ formattedLoadedAt }}</span>
        </div>

        <div class="table-responsive">
          <table class="table report-table mb-0">
            <thead>
              <tr>
                <th>Month</th>
                <th class="text-end">Orders</th>
                <th class="text-end">Clients</th>
                <th class="text-end">New</th>
                <th class="text-end">Returning</th>
                <th class="text-end">Revenue</th>
                <th class="text-end">New client revenue</th>
                <th class="text-end">Returning revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="month in months" :key="month.key" :class="{ 'text-muted': month.ordersCount === 0 }">
                <td class="fw-semibold">{{ month.label }}</td>
                <td class="text-end">{{ month.ordersCount }}</td>
                <td class="text-end">{{ month.totalCompanies }}</td>
                <td class="text-end">{{ month.newCompaniesCount }}</td>
                <td class="text-end">{{ month.returningCompanies }}</td>
                <td class="text-end text-nowrap">{{ formatCurrency(month.totalRevenue) }}</td>
                <td class="text-end text-nowrap">
                  {{ formatCurrency(month.newRevenue) }}
                  <span class="percentage">{{ formatPercent(month.newRevenueShare) }}</span>
                </td>
                <td class="text-end text-nowrap">
                  {{ formatCurrency(month.returningRevenue) }}
                  <span class="percentage">{{ formatPercent(month.returningRevenueShare) }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>Year total</th>
                <th class="text-end">{{ yearSummary.ordersCount }}</th>
                <th class="text-end">—</th>
                <th class="text-end">{{ yearSummary.newClients }}</th>
                <th class="text-end">—</th>
                <th class="text-end text-nowrap">{{ formatCurrency(yearSummary.revenue) }}</th>
                <th class="text-end text-nowrap">{{ formatCurrency(yearSummary.newClientRevenue) }}</th>
                <th class="text-end text-nowrap">{{ formatCurrency(yearSummary.returningClientRevenue) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section aria-labelledby="monthly-details-title">
        <div class="mb-2">
          <h2 id="monthly-details-title" class="h5 mb-1">New clients by month</h2>
          <div class="small text-muted">Company fields come from the current company record; missing Airtable values appear as “—”.</div>
        </div>

        <details
          v-for="month in populatedMonths"
          :key="month.key"
          class="month-panel border rounded bg-white mb-3"
          :open="month.key === latestPopulatedMonthKey"
        >
          <summary class="month-summary">
            <span>
              <strong>{{ month.label }}</strong>
              <span class="text-muted ms-2">{{ month.ordersCount }} orders · {{ formatCurrency(month.totalRevenue) }}</span>
            </span>
            <span class="new-client-pill">{{ month.newCompaniesCount }} new</span>
          </summary>

          <div class="month-content">
            <div class="month-split-grid">
              <div class="split-card">
                <div class="split-title">Clients</div>
                <div class="split-row"><span>Total</span><strong>{{ month.totalCompanies }} <small>{{ formatPercent(1) }}</small></strong></div>
                <div class="split-row"><span>New</span><strong>{{ month.newCompaniesCount }} <small>{{ formatPercent(month.newCompanyShare) }}</small></strong></div>
                <div class="split-row"><span>Returning</span><strong>{{ month.returningCompanies }} <small>{{ formatPercent(month.returningCompanyShare) }}</small></strong></div>
              </div>
              <div class="split-card">
                <div class="split-title">Revenue</div>
                <div class="split-row"><span>Total</span><strong>{{ formatCurrency(month.totalRevenue) }} <small>{{ formatPercent(1) }}</small></strong></div>
                <div class="split-row"><span>New clients</span><strong>{{ formatCurrency(month.newRevenue) }} <small>{{ formatPercent(month.newRevenueShare) }}</small></strong></div>
                <div class="split-row"><span>Returning clients</span><strong>{{ formatCurrency(month.returningRevenue) }} <small>{{ formatPercent(month.returningRevenueShare) }}</small></strong></div>
              </div>
            </div>

            <div v-if="month.newClients.length" class="table-responsive border rounded">
              <table class="table report-table new-clients-table mb-0">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Company size</th>
                    <th class="text-end">Orders</th>
                    <th class="text-end">Revenue this month</th>
                    <th class="text-end">First order revenue</th>
                    <th>SDR</th>
                    <th>Campaign</th>
                    <th>Sector</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="client in month.newClients" :key="client.key">
                    <td class="fw-semibold">
                      <router-link v-if="client.id" :to="`/companies/${client.id}`">{{ client.name }}</router-link>
                      <span v-else>{{ client.name }}</span>
                    </td>
                    <td>{{ client.size }}</td>
                    <td class="text-end">{{ client.ordersCount }}</td>
                    <td class="text-end text-nowrap">{{ formatCurrency(client.revenue) }}</td>
                    <td class="text-end text-nowrap">{{ formatCurrency(client.firstOrderRevenue) }}</td>
                    <td>{{ client.sdr }}</td>
                    <td>{{ client.campaign }}</td>
                    <td>{{ client.sector }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-month">No new clients in {{ month.label }}.</div>
          </div>
        </details>

        <div v-if="!populatedMonths.length" class="border rounded bg-white text-center text-muted p-5">
          No orders found for {{ selectedYear }}.
        </div>
      </section>
    </template>
  </div>

  <div v-else class="alert alert-warning mt-4">
    This report is available to Admin users.
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { fetchRecords } from '@/services/airtable';

const ORDER_STATUSES = [
  'Order: In Progress',
  'Order: Not Started Yet',
  'Order: Completed',
];

export default {
  name: 'ClientsAnalysis',

  data() {
    const currentYear = new Date().getFullYear();
    const queryYear = Number.parseInt(this.$route.query.year, 10);

    return {
      selectedYear: Number.isInteger(queryYear) && queryYear >= 2019 && queryYear <= currentYear ? queryYear : currentYear,
      orders: [],
      loading: false,
      errorMessage: '',
      loadedAt: null,
    };
  },

  computed: {
    ...mapGetters([
      'getProperty',
    ]),

    availableYears() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: currentYear - 2018 }, (_, index) => currentYear - index);
    },

    companies() {
      return this.getProperty('companies') || [];
    },

    companiesById() {
      return new Map(this.companies.map(company => [company.id, company]));
    },

    companiesByName() {
      return new Map(this.companies.map(company => [this.normaliseName(company.fields['Name']), company]));
    },

    monthsElapsed() {
      const now = new Date();
      if (this.selectedYear < now.getFullYear()) return 12;
      if (this.selectedYear > now.getFullYear()) return 0;
      return now.getMonth() + 1;
    },

    months() {
      return Array.from({ length: 12 }, (_, monthIndex) => this.buildMonth(monthIndex));
    },

    populatedMonths() {
      return this.months.filter(month => month.ordersCount > 0);
    },

    latestPopulatedMonthKey() {
      return this.populatedMonths.length ? this.populatedMonths[this.populatedMonths.length - 1].key : '';
    },

    yearSummary() {
      const months = this.months;
      const divisor = this.monthsElapsed || 1;
      const revenue = this.sum(months, month => month.totalRevenue);
      const newClientRevenue = this.sum(months, month => month.newRevenue);
      const returningClientRevenue = this.sum(months, month => month.returningRevenue);
      const firstOrderRevenue = this.sum(months, month => month.firstOrderRevenue);
      const ordersByNewClients = this.sum(months, month => month.newOrdersCount);
      const ordersByReturningClients = this.sum(months, month => month.returningOrdersCount);

      return {
        revenue,
        averageRevenue: revenue / divisor,
        newClients: this.sum(months, month => month.newCompaniesCount),
        newClientsPerMonth: this.sum(months, month => month.newCompaniesCount) / divisor,
        ordersCount: ordersByNewClients + ordersByReturningClients,
        ordersByNewClients,
        ordersByReturningClients,
        ordersByNewClientsPerMonth: ordersByNewClients / divisor,
        ordersByReturningClientsPerMonth: ordersByReturningClients / divisor,
        newClientRevenue,
        returningClientRevenue,
        newClientRevenuePerMonth: newClientRevenue / divisor,
        returningClientRevenuePerMonth: returningClientRevenue / divisor,
        firstOrderRevenue,
        firstOrderRevenueShare: revenue ? firstOrderRevenue / revenue : 0,
        returningClientRevenueShare: revenue ? returningClientRevenue / revenue : 0,
      };
    },

    primaryKpis() {
      const summary = this.yearSummary;
      return [
        { label: 'Yearly revenue', value: this.formatCurrency(summary.revenue), note: `${summary.ordersCount} orders`, help: 'Gross revenue from non-cancelled orders started in the selected year.' },
        { label: 'Avg. revenue / month', value: this.formatCurrency(summary.averageRevenue), note: `${this.monthsElapsed} months`, help: 'Yearly revenue divided by elapsed months for the current year, or 12 for a past year.' },
        { label: 'New clients acquired', value: this.formatNumber(summary.newClients, 0), note: `${this.formatNumber(summary.newClientsPerMonth, 1)} per month`, help: 'Unique companies whose First Order date is in the selected year.' },
        { label: 'Orders by new clients / month', value: this.formatNumber(summary.ordersByNewClientsPerMonth, 1), note: `${summary.ordersByNewClients} total`, help: 'Orders placed in the same calendar month as the client’s first order.' },
        { label: 'Orders by returning clients / month', value: this.formatNumber(summary.ordersByReturningClientsPerMonth, 1), note: `${summary.ordersByReturningClients} total`, help: 'Orders from clients outside the calendar month of their first order.' },
        { label: 'New client revenue / month', value: this.formatCurrency(summary.newClientRevenuePerMonth), note: `${this.formatPercent(summary.revenue ? summary.newClientRevenue / summary.revenue : 0)} of revenue`, help: 'Revenue earned in each client’s acquisition month, averaged over the report period.' },
        { label: 'Returning client revenue / month', value: this.formatCurrency(summary.returningClientRevenuePerMonth), note: `${this.formatPercent(summary.returningClientRevenueShare)} of revenue`, help: 'Revenue from clients outside their acquisition month, averaged over the report period.' },
        { label: 'First-order revenue', value: this.formatCurrency(summary.firstOrderRevenue), note: `${this.formatPercent(summary.firstOrderRevenueShare)} of revenue`, help: 'Revenue from orders whose Order Start Date equals the company First Order date.' },
      ];
    },

    formattedLoadedAt() {
      if (!this.loadedAt) return '—';
      return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(this.loadedAt);
    },
  },

  methods: {
    normaliseName(value) {
      return String(value || '').trim().toLocaleLowerCase();
    },

    first(value) {
      return Array.isArray(value) ? value[0] : value;
    },

    displayValue(value, fallback = '—') {
      if (Array.isArray(value)) {
        const values = value.filter(Boolean);
        return values.length ? values.join(', ') : fallback;
      }
      return value === undefined || value === null || value === '' ? fallback : String(value);
    },

    dateKey(value) {
      const date = this.first(value);
      if (!date) return '';
      const match = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
    },

    monthKey(value) {
      return this.dateKey(value).slice(0, 7);
    },

    companyKey(order) {
      const companyId = this.first(order.fields['Company ID']);
      if (companyId) return companyId;
      return this.normaliseName(this.first(order.fields['Company Lookup'])) || `unassigned-${order.id}`;
    },

    companyRecordFor(order) {
      const companyId = this.first(order.fields['Company ID']);
      if (companyId && this.companiesById.has(companyId)) return this.companiesById.get(companyId);
      return this.companiesByName.get(this.normaliseName(this.first(order.fields['Company Lookup'])));
    },

    companyFirstOrder(order) {
      const company = this.companyRecordFor(order);
      return order.fields['Company First Order'] || company?.fields['First Order'];
    },

    sum(items, getValue) {
      return items.reduce((total, item) => total + (Number(getValue(item)) || 0), 0);
    },

    revenue(order) {
      return Number(order.fields['9 - Gross Revenue in €']) || 0;
    },

    buildMonth(monthIndex) {
      const monthNumber = String(monthIndex + 1).padStart(2, '0');
      const key = `${this.selectedYear}-${monthNumber}`;
      const label = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2000, monthIndex, 1));
      const monthOrders = this.orders.filter(order => this.monthKey(order.fields['Order Start Date']) === key);
      const companyGroups = new Map();

      monthOrders.forEach(order => {
        const companyKey = this.companyKey(order);
        if (!companyGroups.has(companyKey)) companyGroups.set(companyKey, []);
        companyGroups.get(companyKey).push(order);
      });

      const newCompanyGroups = Array.from(companyGroups.entries()).filter(([, companyOrders]) => {
        return this.monthKey(this.companyFirstOrder(companyOrders[0])) === key;
      });
      const newCompanyKeys = new Set(newCompanyGroups.map(([companyKey]) => companyKey));
      const newOrders = monthOrders.filter(order => newCompanyKeys.has(this.companyKey(order)));
      const returningOrders = monthOrders.filter(order => !newCompanyKeys.has(this.companyKey(order)));
      const totalRevenue = this.sum(monthOrders, order => this.revenue(order));
      const newRevenue = this.sum(newOrders, order => this.revenue(order));
      const firstOrderRevenue = this.sum(newOrders, order => (
        this.dateKey(order.fields['Order Start Date']) === this.dateKey(this.companyFirstOrder(order)) ? this.revenue(order) : 0
      ));

      const newClients = newCompanyGroups.map(([companyKey, companyOrders]) => {
        const order = companyOrders[0];
        const company = this.companyRecordFor(order);
        const companyFields = company ? company.fields : {};
        const employeeCount = this.first(companyFields['Apollo Employee Count']) || this.first(order.fields['Company Employee Count']);
        const size = companyFields['Size'] || (employeeCount ? `${employeeCount} employees` : null);
        const campaign = companyFields['Acquisition Channels (after analyzing opps)'] || companyFields['Acquisition Channels in Opps'];
        const sector = companyFields['Sector'] || companyFields['Industry Lookup'] || order.fields['Sector (from Company)'] || order.fields['Company Industry'];
        const sdr = companyFields['Acquired By Lookup'] || order.fields['Company Acquired By Lookup'];

        return {
          key: companyKey,
          id: company ? company.id : this.first(order.fields['Company ID']),
          name: this.displayValue(companyFields['Name'] || this.first(order.fields['Company Lookup']), 'Unassigned company'),
          size: this.displayValue(size),
          ordersCount: companyOrders.length,
          revenue: this.sum(companyOrders, item => this.revenue(item)),
          firstOrderRevenue: this.sum(companyOrders, item => (
            this.dateKey(item.fields['Order Start Date']) === this.dateKey(this.companyFirstOrder(item)) ? this.revenue(item) : 0
          )),
          sdr: this.displayValue(sdr),
          campaign: this.displayValue(campaign),
          sector: this.displayValue(sector),
        };
      }).sort((a, b) => b.revenue - a.revenue);

      const totalCompanies = companyGroups.size;
      const newCompaniesCount = newCompanyGroups.length;
      const returningCompanies = totalCompanies - newCompaniesCount;
      const returningRevenue = totalRevenue - newRevenue;

      return {
        key,
        label,
        ordersCount: monthOrders.length,
        newOrdersCount: newOrders.length,
        returningOrdersCount: returningOrders.length,
        totalCompanies,
        newCompaniesCount,
        returningCompanies,
        newCompanyShare: totalCompanies ? newCompaniesCount / totalCompanies : 0,
        returningCompanyShare: totalCompanies ? returningCompanies / totalCompanies : 0,
        totalRevenue,
        newRevenue,
        returningRevenue,
        firstOrderRevenue,
        newRevenueShare: totalRevenue ? newRevenue / totalRevenue : 0,
        returningRevenueShare: totalRevenue ? returningRevenue / totalRevenue : 0,
        newClients,
      };
    },

    getYearFilter() {
      const start = `${this.selectedYear}-01-01`;
      const end = `${this.selectedYear}-12-31`;
      const statuses = ORDER_STATUSES.map(status => `{Status} = "${status}"`).join(',');

      return `AND(
        OR(${statuses}),
        OR(
          IS_AFTER({Order Start Date}, DATETIME_PARSE('${start}', 'YYYY-MM-DD')),
          {Order Start Date} = DATETIME_PARSE('${start}', 'YYYY-MM-DD')
        ),
        OR(
          IS_BEFORE({Order Start Date}, DATETIME_PARSE('${end}', 'YYYY-MM-DD')),
          {Order Start Date} = DATETIME_PARSE('${end}', 'YYYY-MM-DD')
        )
      )`;
    },

    async fetchReport() {
      if (!this.mixinUserInRoles(['Admin'])) return;

      this.loading = true;
      this.errorMessage = '';

      try {
        this.orders = await fetchRecords(process.env.VUE_APP_OPPORTUNITIES_TABLE_NAME || 'Opportunities', this.getYearFilter());
        this.loadedAt = new Date();
      } catch (error) {
        console.error('Error fetching clients analysis:', error);
        this.errorMessage = error.response?.data?.error?.message || error.message || 'Unknown error';
      } finally {
        this.loading = false;
      }
    },

    changeYear() {
      this.$router.replace({ query: { ...this.$route.query, year: String(this.selectedYear) } }).catch(() => {});
      this.fetchReport();
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(value || 0);
    },

    formatNumber(value, maximumFractionDigits = 1) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value || 0);
    },

    formatPercent(value) {
      return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
    },

    csvCell(value) {
      const text = String(value ?? '');
      return `"${text.replace(/"/g, '""')}"`;
    },

    downloadCsv() {
      const rows = [
        ['Clients Analysis', this.selectedYear],
        ['Yearly Revenue', this.yearSummary.revenue],
        ['Avg. Revenue/Month', this.yearSummary.averageRevenue],
        ['New Clients Acquired', this.yearSummary.newClients],
        ['New Clients Acquired/Month', this.yearSummary.newClientsPerMonth],
        ['Orders by New Clients/Month', this.yearSummary.ordersByNewClientsPerMonth],
        ['Orders by Returning Clients/Month', this.yearSummary.ordersByReturningClientsPerMonth],
        ['Revenue by New Clients/Month', this.yearSummary.newClientRevenuePerMonth],
        ['Revenue by Returning Clients/Month', this.yearSummary.returningClientRevenuePerMonth],
        ['Revenue First Orders of New Clients', this.yearSummary.firstOrderRevenue],
        [],
        ['Month', 'Orders', 'Companies', 'New Companies', 'Returning Companies', 'Revenue', 'New Client Revenue', 'Returning Client Revenue'],
        ...this.months.map(month => [month.label, month.ordersCount, month.totalCompanies, month.newCompaniesCount, month.returningCompanies, month.totalRevenue, month.newRevenue, month.returningRevenue]),
      ];

      this.months.forEach(month => {
        if (!month.newClients.length) return;
        rows.push([], [month.label, 'New clients'], ['Company', 'Company Size', 'Orders', 'Revenue in this Month', 'First Order Revenue', 'SDR', 'Campaign', 'Sector']);
        month.newClients.forEach(client => rows.push([client.name, client.size, client.ordersCount, client.revenue, client.firstOrderRevenue, client.sdr, client.campaign, client.sector]));
      });

      const csv = rows.map(row => row.map(this.csvCell).join(',')).join('\r\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-analysis-${this.selectedYear}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  },

  created() {
    this.mixinCheckUserAuthentication();
    document.title = `Clients Analysis: ${this.selectedYear}`;
    this.fetchReport();
  },
};
</script>

<style scoped>
.clients-analysis {
  --report-ink: #172033;
  --report-muted: #667085;
  --report-accent: #2457d6;
  color: var(--report-ink);
}

.report-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.12em;
}

.year-select {
  width: 96px;
}

.report-loading {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.kpi-card {
  min-height: 126px;
  padding: 18px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03);
}

.kpi-label {
  min-height: 38px;
  color: var(--report-muted);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.kpi-value {
  margin-top: 7px;
  font-size: clamp(1.35rem, 2vw, 1.8rem);
  font-weight: 700;
  letter-spacing: -0.035em;
}

.kpi-note {
  margin-top: 3px;
  color: var(--report-muted);
  font-size: 0.75rem;
}

.section-heading {
  padding: 18px 20px;
  background: #fbfcfe;
  border-bottom: 1px solid rgba(222, 226, 230, 0.7);
}

.report-table {
  font-size: 0.82rem;
}

.report-table th,
.report-table td {
  padding: 11px 14px;
  vertical-align: middle;
  border-color: rgba(222, 226, 230, 0.55);
}

.report-table thead th {
  color: #526070;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.report-table tfoot {
  background: #f8f9fb;
}

.percentage {
  display: block;
  color: var(--report-muted);
  font-size: 0.68rem;
}

.month-panel {
  overflow: hidden;
}

.month-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  cursor: pointer;
  list-style: none;
  background: #fbfcfe;
}

.month-summary::-webkit-details-marker {
  display: none;
}

.month-summary::before {
  content: '›';
  margin-right: 8px;
  color: var(--report-muted);
  font-size: 1.2rem;
  transition: transform 0.15s ease;
}

details[open] > .month-summary::before {
  transform: rotate(90deg);
}

.month-summary > span:first-of-type {
  flex: 1;
}

.new-client-pill {
  padding: 4px 9px;
  color: #1849a9;
  background: #eff4ff;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.month-content {
  padding: 18px;
  border-top: 1px solid rgba(222, 226, 230, 0.7);
}

.month-split-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.split-card {
  padding: 14px 16px;
  background: #f8f9fb;
  border-radius: 8px;
}

.split-title {
  margin-bottom: 7px;
  color: var(--report-muted);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}

.split-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  font-size: 0.82rem;
}

.split-row small {
  margin-left: 6px;
  color: var(--report-muted);
  font-weight: 400;
}

.new-clients-table {
  min-width: 1040px;
}

.empty-month {
  padding: 24px;
  color: var(--report-muted);
  text-align: center;
  background: #f8f9fb;
  border-radius: 8px;
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .kpi-grid,
  .month-split-grid {
    grid-template-columns: 1fr;
  }

  .month-summary {
    align-items: flex-start;
  }
}
</style>
